import { ArrowUp, ArrowDown, ArrowUpDown, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BookmarkFilledIcon } from '@radix-ui/react-icons'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import RoasAnalysisChart from './roas-analysis-chart'
import { useRoasNoteStore, useRoasStore } from '@/stores/roas'
import { getWeek } from '@/lib/date-utils'
import { useMemo } from 'react'
import useSWR from 'swr'
import { formatPercent, formatUSD } from '@/lib/format-utils'
import VisibleDateTrackerCell from '@/components/roi/VisibleDateTrackerCell'
import {
  getDisplayableRoasBaseline,
  TOTAL_FALLBACK_STANDARD_MODE,
} from '@/lib/dr-utils'
import { ROAS_STANDARD_DAYS } from '@/features/roas/constants/roas-days'
import dayjs from 'dayjs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const InstallDateHeader = ({ handleSort, sorts }) => {
  const currentSort = sorts?.find((s) => s.code === 'install_date')
  const isAsc = currentSort?.order === 'asc'
  const isDesc = currentSort?.order === 'desc'
  return (
    <div
      className="flex items-center justify-start gap-1 cursor-pointer"
      onClick={() => {
        const newSorts = [
          {
            code: 'install_date',
            order: isAsc ? 'desc' : 'asc',
          },
        ]
        handleSort(newSorts)
      }}
    >
      <span className="text-base text-black">Install Date</span>
      {isAsc && <ArrowUp size={20} />}
      {isDesc && <ArrowDown size={20} />}
      {!isAsc && !isDesc && <ArrowUpDown size={20} className="text-gray-400" />}
    </div>
  )
}

const InstallDateCell = ({
  row,
  campaignId,
  day0RoasLine,
  day0RoasLineTotalFallback,
  compareRoas,
  summaryMode,
  allData,
  mainRowIndex,
}) => {
  const roas = row.original
  const [, weekDay] = getWeek(roas.install_date)
  const { showNotes } = useRoasNoteStore()
  const { displayMode, dayMode } = useRoasStore()
  const { data: notes } = useSWR([
    campaignId ? `/campaigns/${campaignId}/notes` : null,
  ])
  const hasNote = useMemo(
    () =>
      notes?.some(
        (n) => n.date === Number(roas.install_date.replaceAll('-', '')),
      ),
    [notes, roas.install_date],
  )

  // 查询 day0 af_country 收入分布，用于异国收入告警
  // SWR 对相同 key 做缓存，同一 campaign 所有行共享一个请求
  const { data: countryRevenue } = useSWR(
    campaignId ? [`/troas/${campaignId}/day0-country-revenue`] : null,
  )

  // 计算当前 install_date 的异国收入占比
  const countryMismatch = useMemo(() => {
    if (!countryRevenue?.items || !countryRevenue?.campaignCountry) return null
    const dateData = countryRevenue.items.find(
      (item) => item.installDate === roas.install_date,
    )
    if (!dateData) return null

    const campaignCountry = countryRevenue.campaignCountry
    let campaignCountryRevenue = 0
    let nonCampaignCountryRevenue = 0

    for (const c of dateData.countries) {
      if (c.country === campaignCountry) {
        campaignCountryRevenue += c.revenue
      } else {
        nonCampaignCountryRevenue += c.revenue
      }
    }

    if (campaignCountryRevenue <= 0) return null

    const mismatchRate = nonCampaignCountryRevenue / campaignCountryRevenue
    if (mismatchRate <= 0.02) return null

    return {
      mismatchRate,
      campaignCountry,
      campaignCountryRevenue,
      nonCampaignCountryRevenue,
      countries: [...dateData.countries].sort((a, b) => b.revenue - a.revenue),
    }
  }, [countryRevenue, roas.install_date])

  const handleNote = () => {
    showNotes({ date: roas.install_date, cid: campaignId })
  }

  // 检查是否有 baseline 变更
  const dateFormatted = roas.install_date
  const changes = []
  
  if (summaryMode === 'daily' || !summaryMode) {
    if (day0RoasLine && typeof day0RoasLine === 'object') {
      const prevDate = dayjs(dateFormatted).subtract(1, 'day').format('YYYY-MM-DD')
      ROAS_STANDARD_DAYS.forEach((day) => {
        const hasStartDate = day0RoasLine?.[day]?.[dateFormatted] !== undefined
        if (!hasStartDate) return

        const baseline = getDisplayableRoasBaseline(
          day0RoasLine,
          day0RoasLineTotalFallback,
          day,
          dateFormatted,
          TOTAL_FALLBACK_STANDARD_MODE,
        )
        const prevBaseline = getDisplayableRoasBaseline(
          day0RoasLine,
          day0RoasLineTotalFallback,
          day,
          prevDate,
          TOTAL_FALLBACK_STANDARD_MODE,
        )
        if (baseline == null || prevBaseline == null) return
        if (baseline !== prevBaseline) {
          changes.push({ day, from: prevBaseline, to: baseline })
        }
      })
    }
  }
  
  const hasChange = changes.length > 0 && displayMode !== 'multiplier'

  // 将日期转换为 YYYYMMDD 格式用于 VisibleDateTrackerCell
  const dateForTracker = dateFormatted.replace(/-/g, '')
  const displayLabel = roas.displayDate || roas.install_date
  const isDaily = summaryMode === 'daily' || !summaryMode

  return (
    <VisibleDateTrackerCell date={dateForTracker}>
      <div className="relative flex flex-col pl-5">
        {hasChange && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute bottom-1 left-1 w-3 h-3 bg-green-500 rounded-full" />
              </TooltipTrigger>
              <TooltipContent>
                <p>ROAS Baseline Changed:</p>
                {changes.map((change) => (
                  <div key={change.day}>
                    <strong>Day {change.day}:</strong> {change.from} -&gt; {change.to}
                  </div>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <ChartCodeViewer
          triggerButton={
            <Button
              variant="link"
              className="font-medium underline underline-offset-4 hover:text-primary justify-start px-0 text-left text-base text-black"
              onClick={(e) => e.stopPropagation()}
            >
              {displayLabel}
            </Button>
          }
        >
        <RoasAnalysisChart
            install_date={row.original.install_date}
            displayDate={displayLabel}
            days={row.original.days}
            cumulativeRevenueDays={row.original.cumulativeRevenueDays}
            cumulativePredictRevenueDays={row.original.cumulativePredictRevenueDays}
            cost={row.original.cost}
            dayCosts={row.original.dayCosts}
            allModelsData={row.original.allModelsData}
            allModelsDayCosts={row.original.allModelsDayCosts}
            allModelsCumulativeData={row.original.allModelsCumulativeData}
            compareRoas={compareRoas}
            mainRowIndex={mainRowIndex}
            compareDays={!Array.isArray(compareRoas) ? compareRoas?.dailyData?.find(
              (item) => item.install_date === roas.install_date
            )?.days : undefined}
            compareCumulativeRevenueDays={!Array.isArray(compareRoas) ? compareRoas?.dailyData?.find(
              (item) => item.install_date === roas.install_date
            )?.cumulativeRevenueDays : undefined}
            compareCost={!Array.isArray(compareRoas) ? compareRoas?.dailyData?.find(
              (item) => item.install_date === roas.install_date
            )?.cost : undefined}
            compareDayCosts={!Array.isArray(compareRoas) ? compareRoas?.dailyData?.find(
              (item) => item.install_date === roas.install_date
            )?.dayCosts : undefined}
            allData={allData}
            summaryMode={summaryMode}
            displayMode={displayMode}
            dayMode={dayMode}
            day0RoasLine={day0RoasLine}
            day0RoasLineTotalFallback={day0RoasLineTotalFallback}
          />
        </ChartCodeViewer>
        {(isDaily || countryMismatch) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {isDaily && <>星期{weekDay}</>}
            {isDaily && (
              <Button
                variant="ghost"
                size="sm"
                className={cn('p-0 h-4 w-4', hasNote && 'text-green-500')}
                onClick={handleNote}
              >
                <BookmarkFilledIcon />
              </Button>
            )}
            {countryMismatch && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help text-amber-500">
                      <TriangleAlert className="h-4 w-4" fill="currentColor" stroke="currentColor" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[260px] p-3">
                    <p className="font-semibold mb-2 border-b pb-1 text-sm text-foreground">
                      Non-target country revenue: {(countryMismatch.mismatchRate * 100).toFixed(1)}%
                    </p>
                    <div className="text-xs space-y-1 mt-1">
                      <div className="flex justify-between gap-6">
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">{countryMismatch.campaignCountry} (target)</span>
                        <span className="font-medium">${countryMismatch.campaignCountryRevenue.toFixed(2)}</span>
                      </div>
                      {countryMismatch.countries
                        .filter((c) => c.country !== countryMismatch.campaignCountry)
                        .map((c) => (
                          <div key={c.country} className="flex justify-between gap-6">
                            <span className="text-red-500 dark:text-red-400">{c.country}</span>
                            <span className="text-muted-foreground">${c.revenue.toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
    </VisibleDateTrackerCell>
  )
}

const InstallsCell = ({ installs, totalInstalls }) => {
  return (
    <div className="flex flex-col">
      <div className="text-right text-base text-black">{installs}</div>
      <div className="text-right text-base text-xs text-gray-500">
        {totalInstalls}/{formatPercent(installs / totalInstalls, 1, 1)}
      </div>
    </div>
  )
}
// Cell components for ali-react-table-yet
export const MoneyCell = ({ value }) => {
  if (!value) {
    return (
      <div className="text-right text-base text-black">
        <span className="text-muted-foreground">-</span>
      </div>
    )
  }
  return (
    <div className="text-right text-base text-black">{formatUSD(value)}</div>
  )
}

export const IndexColumn = {
  code: 'index',
  name: '#',
  width: 60,
  align: 'right',
  lock: true,
  render: (value, row, rowIndex) => (
    <div className="text-right text-base text-black">{rowIndex}</div>
  ),
}

export const InstallDateColumn = (
  campaignId,
  handleSort,
  sorts,
  day0RoasLine,
  day0RoasLineTotalFallback,
  compareRoas,
  summaryMode,
  allData,
  getMainRowIndex,
) => ({
  code: 'install_date',
  width: 170,
  lock: true,
  features: { sort: true },
  title: <InstallDateHeader handleSort={handleSort} sorts={sorts} />,
  render: (value, row) => (
    <InstallDateCell
      row={{ original: row }}
      campaignId={campaignId}
      day0RoasLine={day0RoasLine}
      day0RoasLineTotalFallback={day0RoasLineTotalFallback}
      compareRoas={compareRoas}
      summaryMode={summaryMode}
      allData={allData}
      mainRowIndex={typeof getMainRowIndex === 'function' ? getMainRowIndex(row) : undefined}
    />
  ),
})

export const InstallsColumn = (isTotal) => ({
  code: 'installs',
  name: <span className="text-base text-black">Installs</span>,
  width: isTotal ? 100 : 120,
  align: 'right',
  render: (value, row) =>
    isTotal ? (
      <div className="text-right text-base text-black">{value}</div>
    ) : (
      <InstallsCell installs={value} totalInstalls={row.totalCampInstalls} />
    ),
})

export const ActiveInstallsColumn = (isTotal) => ({
  code: 'activeInstalls',
  name: (
    <div className="flex flex-col items-end">
      <span className="text-base text-black">Installs</span>
      <span className="text-xs text-gray-500">(Firebase)</span>
    </div>
  ),
  width: isTotal ? 100 : 120,
  align: 'right',
  render: (value, row) =>
    isTotal ? (
      <div className="text-right text-base text-black">{value}</div>
    ) : (
      <div className="flex flex-col">
        <div className="text-right text-base text-black">{value}</div>
        <div className="text-right text-base text-xs text-gray-500">
          {formatPercent(value / row.installs, 1, 1)}
        </div>
      </div>
    ),
})

export const RevenueColumn = {
  code: 'revenue',
  width: 100,
  align: 'right',
  title: <span className="text-base text-black">Revenue</span>,
  render: (value) => <MoneyCell value={value} />,
}
